import type { MultiLangField } from "helpers/functions";
import type { IBlogByAlias } from "entities/market";

// Блог переехал из старой админки в Strapi целиком. Старый бэкенд отдавал все
// языки в одной записи (title.en/ru/am), Strapi держит по записи на локаль —
// здесь три ответа сворачиваются обратно в привычную форму, поэтому экраны
// продолжают читать title/subtitle как объекты, а поиск ищет по всем языкам.

// Язык приложения -> локаль CMS. Армянский здесь "am", в Strapi "hy".
export const BLOG_LOCALES: [keyof MultiLangField, string][] = [
  ["en", "en"],
  ["ru", "ru"],
  ["am", "hy"],
];

// Списку нужны только поля карточки. Иначе Strapi присылает ещё и полный текст
// каждой статьи — ~180KB на локаль вместо ~14KB. background_image и categories
// приходят всё равно, а если запросить их явно, эндпоинт отвечает 500.
export const BLOG_LIST_FIELDS = {
  "fields[0]": "title",
  "fields[1]": "alias",
  "fields[2]": "date",
  "fields[3]": "subtitle",
  "fields[4]": "time_to_read",
};

interface StrapiBlogItem {
  id: number;
  title?: string | null;
  subtitle?: string | null;
  summary?: string | null;
  alias: string;
  date?: string | null;
  views?: number | null;
  time_to_read?: number | null;
  background_image?: string | null;
  categories?: { id: number; name?: string | null }[];
}

const ml = (value?: string | null): MultiLangField => {
  const text = value || "";
  return { en: text, ru: text, am: text };
};

// Старые записи хранят "YYYY.MM.DD", на этот формат завязаны сортировка и
// moment в UI; Strapi отдаёт ISO "YYYY-MM-DD".
const toLegacyDate = (date?: string | null) => (date || "").replace(/-/g, ".");

// Название категории приходит строкой и только у английской записи —
// переводы её не несут. getMLValue ждёт объект.
const toLegacyCategories = (categories?: StrapiBlogItem["categories"]) =>
  (categories || []).map((c) => ({ id: c.id, name: ml(c.name) }));

type LocaleBatch = readonly [keyof MultiLangField, StrapiBlogItem[]];

export function foldBlogLocales(
  batches: readonly LocaleBatch[],
): IBlogByAlias[] {
  const merged = new Map<string, IBlogByAlias>();
  for (const [lang, records] of batches) {
    for (const item of records) {
      if (!item.alias) continue;
      let entry = merged.get(item.alias);
      if (!entry) {
        entry = {
          id: item.id,
          alias: item.alias,
          title: {} as MultiLangField,
          subtitle: {} as MultiLangField,
          authors: [],
          categories: [],
          date: toLegacyDate(item.date),
          views: item.views || 0,
          time_to_read: item.time_to_read || 0,
          background_image: item.background_image || "",
          content_en: "",
          content_ru: "",
          content_am: "",
        };
        merged.set(item.alias, entry);
      }
      entry.title[lang] = item.title || "";
      entry.subtitle[lang] = item.subtitle || "";
      if (lang === "en") entry.content_en = item.summary || "";
      if (lang === "ru") entry.content_ru = item.summary || "";
      if (lang === "am") entry.content_am = item.summary || "";
      // Дата, обложка и категории надёжно заполнены только у английской записи.
      if (!entry.date) entry.date = toLegacyDate(item.date);
      if (!entry.time_to_read) entry.time_to_read = item.time_to_read || 0;
      if (!entry.background_image && item.background_image) {
        entry.background_image = item.background_image;
      }
      if (!entry.categories.length && item.categories?.length) {
        entry.categories = toLegacyCategories(item.categories);
      }
    }
  }
  return Array.from(merged.values()).sort((a, b) =>
    (b.date || "").localeCompare(a.date || ""),
  );
}

// Отдельного эндпоинта со списком категорий блога в Strapi нет — собираем из
// самих постов.
export function collectBlogCategories(items: StrapiBlogItem[]) {
  const seen = new Map<number, { id: number; name: MultiLangField }>();
  for (const item of items) {
    for (const c of item.categories || []) {
      if (!seen.has(c.id)) seen.set(c.id, { id: c.id, name: ml(c.name) });
    }
  }
  return Array.from(seen.values());
}
